import React, { useState, useCallback } from 'react';
import { OUTFIT_OPTIONS, BACKGROUND_OPTIONS, POSE_OPTIONS } from '../constants';
import { TEMPLATE_CATEGORIES, getTemplateById } from '../constants/templates';
import { MagicWandIcon, ResetIcon, CloseIcon, UploadIcon } from './icons';
import { TemplatePreview } from './TemplatePreview';

interface ControlPanelProps {
  onGenerate: (customOutfit: { data: string; mimeType: string } | null, customBackgroundImage: { data: string; mimeType: string } | null) => void;
  onReset: () => void;
  isLoading: boolean;
  error: string | null;
  isPaidUser: boolean | null;
  isAnonymous: boolean;
  userPlan: UserPlan | null;
  scrollToPlanInfo?: () => void;

  selectedOutfitId: string;
  onOutfitIdChange: (id: string) => void;
  customOutfitText: string;
  onCustomOutfitTextChange: (text: string) => void;
  customOutfitImage: { data: string; mimeType: string; } | null;
  onCustomOutfitImageChange: (image: { data: string; mimeType: string; } | null) => void;

  backgroundOption: string;
  onBackgroundOptionChange: (option: string) => void;
  customBackground: string;
  onCustomBackgroundChange: (prompt: string) => void;
  customBackgroundImage: { data: string; mimeType: string } | null;
  onCustomBackgroundImageChange: (image: { data: string; mimeType: string } | null) => void;
  selectedPoseId: string;
  onPoseIdChange: (id: string) => void;
  availableTemplates?: any[];
  isTrialMode?: boolean; // TrialPageで使用する場合のフラグ
}


const outfits = [
  { id: 'suit_blue', name: 'ビジネススーツ（ネイビー）', desc: '信頼感のあるネイビーのスーツ', isPremium: false },
  { id: 'suit_black', name: 'ビジネススーツ（ブラック）', desc: '上品なブラックのスーツ', isPremium: false },
  { id: 'suit_light_gray', name: 'ビジネススーツ（ライトグレー）', desc: '明るいライトグレーのスーツ', isPremium: false },
  { id: 'turtleneck_black', name: 'タートルネック（ブラック）', desc: 'スタイリッシュな黒のタートルネック', isPremium: false },
  { id: 'shirt_white', name: 'カジュアルなシャツ（ホワイト）', desc: '清潔感のある白いカジュアルシャツ', isPremium: false },
  { id: 'aloha_shirt', name: 'アロハシャツ', desc: '明るく華やかなアロハシャツ', isPremium: false },
  { id: 'custom_text', name: 'その他（テキストで指定）', desc: 'カスタム衣装', isPremium: false },
  { id: 'custom_outfit_image', name: '⭐️その他（衣装を添付する）', desc: 'オリジナル衣装を添付', isPremium: true }
];

const poses = [
  { id: 'front', name: '正面', desc: 'まっすぐ前を向いたポーズ' },
  { id: 'slight_left', name: 'やや左向き', desc: '少し左を向いた自然なポーズ' },
  { id: 'slight_right', name: 'やや右向き', desc: '少し右を向いた自然なポーズ' },
  { id: 'left', name: '左向き', desc: '左を向いたプロフィール' },
  { id: 'right', name: '右向き', desc: '右を向いたプロフィール' },
  { id: 'lookback', name: '見返り', desc: '肩越しに振り返る魅力的なポーズ' }
];

// 無料ユーザー用の背景テンプレート（カスタム画像以外すべて利用可能）
const freeTemplates = [
  // ビジネス背景
  { id: 'studio_white', name: 'スタジオ（ホワイト）', description: '清潔感のある白いスタジオ背景' },
  { id: 'studio_gray', name: 'スタジオ（グレー）', description: '落ち着いたグレーのスタジオ背景' },
  { id: 'studio_dark_gray', name: 'スタジオ（ダークグレー）', description: '落ち着いたダークグレーのスタジオ背景' },
  { id: 'studio_light_purple', name: 'スタジオ（ライトパープル）', description: '上品なライトパープルのスタジオ背景' },
  { id: 'studio_light_pink', name: 'スタジオ（ライトピンク）', description: '優しいライトピンクのスタジオ背景' },
  { id: 'studio_light_blue', name: 'スタジオ（ライトブルー）', description: '爽やかなライトブルーのスタジオ背景' },
  // クリエイティブ背景
  { id: 'white_wood', name: '白の木目', description: 'ナチュラルで上品な白い木目調' },
  { id: 'salon_interior', name: '美容室', description: 'プロフェッショナルな美容室の雰囲気' },
  { id: 'cafe_cozy', name: '落ち着いたカフェ', description: '温かみのあるカフェの雰囲気' },
  { id: 'concert_hall', name: 'コンサートホール', description: '華やかなコンサートホールの雰囲気' },
  { id: 'beach_sunset', name: 'ビーチ・夕焼け', description: 'ロマンチックなビーチの夕焼け' },
  { id: 'urban_street', name: '都会の街並み', description: 'モダンな都市の雰囲気' },
  // カスタムテキスト（無料でも利用可能）
  { id: 'custom_template', name: 'その他（テキストで指定）', description: 'カスタムテンプレート' }
];

// 無料ユーザー用の制限された衣装
const freeOutfits = [
  { id: 'suit_blue', name: 'ビジネススーツ（ネイビー）', desc: '信頼感のあるネイビーのスーツ', isPremium: false },
  { id: 'suit_black', name: 'ビジネススーツ（ブラック）', desc: '上品なブラックのスーツ', isPremium: false },
  { id: 'suit_light_gray', name: 'ビジネススーツ（ライトグレー）', desc: '明るいライトグレーのスーツ', isPremium: false },
  { id: 'turtleneck_black', name: 'タートルネック（ブラック）', desc: 'スタイリッシュな黒のタートルネック', isPremium: false },
  { id: 'shirt_white', name: 'カジュアルなシャツ（ホワイト）', desc: '清潔感のある白いカジュアルシャツ', isPremium: false },
  { id: 'custom_text', name: 'その他（テキストで指定）', desc: 'カスタム衣装', isPremium: false },
];

const BackgroundUploader: React.FC<{ onUpload: (data: string, mimeType: string) => void; }> = ({ onUpload }) => {
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック（5MB制限）
    if (file.size > 5 * 1024 * 1024) {
      alert('ファイルサイズが大きすぎます。5MB以下の画像を選択してください。');
      return;
    }

    // ファイル形式チェック
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください。');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        onUpload(result, file.type);
      }
    };
    reader.readAsDataURL(file);
  }, [onUpload]);

  return (
    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        id="background-upload"
      />
      <label htmlFor="background-upload" className="cursor-pointer">
        <UploadIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <p className="text-slate-600 font-medium">背景画像をアップロード</p>
        <p className="text-sm text-slate-500 mt-1">JPG、PNG、WebP形式（5MB以下）</p>
      </label>
    </div>
  );
};

const OutfitUploader: React.FC<{ onUpload: (data: string, mimeType: string) => void; }> = ({ onUpload }) => {
    const [error, setError] = useState<string | null>(null);

    const handleFile = (file: File | undefined) => {
        if (!file) return;

        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            setError('無効なファイル形式です。JPEG, PNG, WEBPをアップロードしてください。');
            return;
        }

        if (file.size > 4 * 1024 * 1024) { // 4MB limit for outfit
            setError('ファイルサイズが大きすぎます。4MB未満の画像をアップロードしてください。');
            return;
        }

        setError(null);
        const reader = new FileReader();
        reader.onload = (loadEvent) => {
            const base64String = loadEvent.target?.result as string;
            onUpload(base64String, file.type);
        };
        reader.onerror = () => {
            setError('ファイルの読み込みに失敗しました。');
        };
        reader.readAsDataURL(file);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFile(event.target.files?.[0]);
    };

    const handleDragOver = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
    }, []);

    const handleDrop = useCallback((event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        handleFile(event.dataTransfer.files?.[0]);
    }, [onUpload]);

    return (
        <div>
            <label
                htmlFor="outfit-upload"
                className="relative block w-full border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-slate-100/50 transition-colors"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <div className="flex flex-col items-center justify-center space-y-2 text-slate-500">
                    <UploadIcon className="w-8 h-8" />
                    <span className="text-sm font-medium">
                        クリックまたはドラッグ＆ドロップ
                    </span>
                    <span className="text-xs">PNG, JPG, WEBP (最大4MB)</span>
                </div>
                <input
                    id="outfit-upload"
                    name="outfit-upload"
                    type="file"
                    className="sr-only"
                    accept="image/png, image/jpeg, image/webp"
                    onChange={handleFileChange}
                />
            </label>
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </div>
    );
};

export const ControlPanel: React.FC<ControlPanelProps> = ({
  onGenerate,
  onReset,
  isLoading,
  error,
  isPaidUser,
  isAnonymous,
  userPlan,
  scrollToPlanInfo,
  selectedOutfitId,
  onOutfitIdChange,
  customOutfitText,
  onCustomOutfitTextChange,
  customOutfitImage,
  onCustomOutfitImageChange,
  backgroundOption,
  onBackgroundOptionChange,
  customBackground,
  onCustomBackgroundChange,
  customBackgroundImage,
  onCustomBackgroundImageChange,
  selectedPoseId,
  onPoseIdChange,
  availableTemplates,
  isTrialMode = false
}) => {
  const handleGenerateClick = () => {
    onGenerate(customOutfitImage, customBackgroundImage);
  };

  const handleCustomOutfitUpload = (base64Image: string, mimeType: string) => {
    onCustomOutfitImageChange({ data: base64Image, mimeType });
  };

  const handleCustomBackgroundImageUpload = (base64Image: string, mimeType: string) => {
    onCustomBackgroundImageChange({ data: base64Image, mimeType });
  };

  // 無料ユーザー向けの機能制限チェック
  const isFeatureRestricted = (featureId: string): boolean => {
    // 有料ユーザーは制限なし
    if (isPaidUser) return false;
    
    // 無料ユーザー（匿名含む）は以下の機能を制限
    const restrictedFeatures = [
      'custom_outfit_image', // カスタム衣装画像
      'custom_background_image', // カスタム背景画像
      // 'custom_text', // カスタムテキスト - 無料体験でも使用可能
      // 'custom_background' // カスタム背景テキスト - 無料体験でも使用可能
    ];
    
    return restrictedFeatures.includes(featureId);
  };

  // プラン別のクレジット上限を取得
  const getCreditLimit = () => {
    if (!isPaidUser) {
      return 3; // 無料体験は3回
    }
    switch (userPlan) {
      case 'standard': return 10;   // ¥500/買い切り → 10枚
      case 'pro': return 25;        // ¥1,000/買い切り → 25枚
      case 'pro_max': return 200;   // ¥5,000/月 → 200枚
      default: return 3;
    }
  };

  const remainingCredits = userPlan?.creditsRest || 0;
  
  // デバッグ用：クレジット計算をコンソールに出力
  console.log('ControlPanel Credit Debug:', {
    isPaidUser,
    userPlan: userPlan?.plan,
    remainingCredits,
    userPlanCreditsRest: userPlan?.creditsRest
  });
  
  const isGenerateDisabled = 
    (selectedOutfitId === 'custom_text' && !customOutfitText) || 
    (backgroundOption === 'custom' && !customBackground) ||
    isLoading ||
    remainingCredits <= 0;

  // デバッグ用：ボタンが無効になる理由をコンソールに出力
  React.useEffect(() => {
    console.log('ControlPanel Debug:', {
      selectedOutfitId,
      customOutfitText,
      backgroundOption,
      customBackground,
      isLoading,
      isPaidUser,
      isAnonymous,
      userPlan: userPlan ? { plan: userPlan.plan, creditsRest: userPlan.creditsRest } : null,
      remainingCredits,
      isGenerateDisabled,
      isTrialMode,
      isFeatureRestrictedCustomText: isFeatureRestricted('custom_text'),
      timestamp: new Date().toLocaleTimeString(),
      disabledReasons: {
        customOutfitMissing: selectedOutfitId === 'custom_outfit' && !customOutfitText,
        customBackgroundMissing: backgroundOption === 'custom' && !customBackground,
        isLoading,
        noCredits: remainingCredits <= 0
      }
    });
  }, [selectedOutfitId, customOutfitText, backgroundOption, customBackground, isLoading, isPaidUser, isAnonymous, userPlan, remainingCredits, isGenerateDisabled, isTrialMode]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 shadow-sm space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900">スタイルをカスタマイズ</h2>
        <button
          onClick={onReset}
          className="flex items-center space-x-2 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
          aria-label="リセット"
        >
          <ResetIcon className="w-4 h-4" />
          <span>リセット</span>
        </button>
      </div>

      {/* クレジット表示 */}
      {(isPaidUser || !isPaidUser) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                {isPaidUser ? (
                  userPlan === 'standard' ? 'Standard プラン' : 
                  userPlan === 'pro' ? 'Pro プラン' : 'Pro Max プラン'
                ) : '無料体験'}
              </h3>
              <p className="text-sm text-blue-700">
                残りクレジット: <span className="font-bold">{remainingCredits}</span> 回
              </p>
            </div>
            <div className="text-right">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center border-2 border-blue-300">
                <span className="text-sm font-bold text-blue-900">
                  {remainingCredits}
                </span>
              </div>
            </div>
          </div>
          {remainingCredits <= 0 && (
            <div className="mt-2">
              <p className="text-sm text-red-600 font-medium">
                {isPaidUser ? 'クレジットが不足しています。プランをアップグレードしてください。' : '無料体験の制限に達しました。有料プランにアップグレードしてください。'}
              </p>
              {scrollToPlanInfo && (
                <button
                  onClick={scrollToPlanInfo}
                  className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 block"
                >
                  → プラン情報を見る
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div>
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">1. 背景</h3>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            背景テンプレート
          </label>
          <select
            value={backgroundOption}
            onChange={(e) => {
              const templateId = e.target.value;
              
              // 無料ユーザーが制限されたオプションを選択しようとした場合は阻止
              if (templateId === 'custom_image' && !isPaidUser) {
                console.log('🚫 無料ユーザーがカスタム画像を選択しようとしました');
                alert('この機能は有料プランでのみご利用いただけます。プランをアップグレードしてください。');
                return;
              }
              
              // 背景変更時に、custom_image以外が選択された場合はカスタム画像をクリア
              if (templateId !== 'custom_image' && customBackgroundImage) {
                console.log('🧹 Clearing custom background image');
                onCustomBackgroundImageChange(null);
              }
              
              onBackgroundOptionChange(templateId);
            }}
            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {(() => {
              // 衣装フィルタリングと同じパターンで背景をフィルタリング
              const allTemplates = availableTemplates || TEMPLATE_CATEGORIES.flatMap(cat => cat.templates);
              const filteredTemplates = isTrialMode ? freeTemplates : (isPaidUser ? allTemplates : freeTemplates);
              
              console.log('🎨 背景選択肢フィルタリング:', {
                isPaidUser,
                isTrialMode,
                availableTemplates: availableTemplates?.length,
                freeTemplates: freeTemplates.length,
                allTemplates: allTemplates.length,
                filteredTemplates: filteredTemplates.length,
                filterLogic: isTrialMode ? 'freeTemplates' : (isPaidUser ? 'allTemplates' : 'freeTemplates'),
                templates: allTemplates.map(t => ({ id: t.id, name: t.name })),
                filteredTemplatesList: filteredTemplates.map(t => ({ id: t.id, name: t.name }))
              });
              
              return (
                <>
                  {/* ビジネス背景 */}
                  <optgroup label="ビジネス背景">
                    {TEMPLATE_CATEGORIES.find(cat => cat.id === 'business')?.templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </optgroup>
                  
                  {/* クリエイティブ背景 */}
                  <optgroup label="クリエイティブ背景">
                    {TEMPLATE_CATEGORIES.find(cat => cat.id === 'creative')?.templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </optgroup>
                  
                  {/* カスタム背景 */}
                  <optgroup label="カスタム背景">
                    <option value="custom_template">その他（テキストで指定）</option>
                    <option 
                      value="custom_image" 
                      disabled={!isPaidUser}
                      style={{ 
                        color: !isPaidUser ? '#9ca3af' : 'inherit',
                        backgroundColor: !isPaidUser ? '#f9fafb' : 'inherit'
                      }}
                    >
                      ⭐️その他（写真を添付する）{!isPaidUser ? ' - 有料プラン限定' : ''}
                    </option>
                  </optgroup>
                </>
              );
            })()}
          </select>
          
          {/* テンプレートの説明を表示（カスタム以外） */}
          {backgroundOption && backgroundOption !== 'custom_template' && backgroundOption !== 'custom_image' && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                {availableTemplates 
                  ? availableTemplates.find(t => t.id === backgroundOption)?.description || '説明なし'
                  : TEMPLATE_CATEGORIES
                      .flatMap(cat => cat.templates)
                      .find(t => t.id === backgroundOption)?.description || '説明なし'}
              </p>
            </div>
          )}
        </div>
        
        {/* カスタムテキスト入力（その他（テキストで指定）） */}
        {backgroundOption === 'custom_template' && !isFeatureRestricted('custom_background') && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              カスタム背景の説明
            </label>
            <textarea
              rows={3}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="例: モダンな建築の図書館、背景はぼかす"
              value={customBackground}
              onChange={(e) => {
                console.log('カスタム背景入力:', e.target.value);
                onCustomBackgroundChange(e.target.value);
              }}
            />
            <p className="text-xs text-slate-500 mt-1">
              背景の詳細な説明を入力してください
            </p>
          </div>
        )}

        {backgroundOption === 'custom_template' && isFeatureRestricted('custom_background') && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <span className="font-medium">🔒 無料ユーザーは制限されています</span><br />
              カスタム背景機能を使用するには、有料プランにアップグレードしてください。
            </p>
          </div>
        )}
        
        {/* カスタム画像アップロード（その他（画像を添付）） */}
        {backgroundOption === 'custom_image' && isPaidUser && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-slate-700 mb-2">背景画像をアップロード</h3>
            <p className="text-sm text-slate-500 mb-4">
              オリジナルの背景画像をアップロードして、その背景で撮影されたような写真を生成できます。
            </p>
            {!customBackgroundImage ? (
              <BackgroundUploader onUpload={handleCustomBackgroundImageUpload} />
            ) : (
              <div className="relative group">
                <img 
                  src={customBackgroundImage.data} 
                  alt="Custom background" 
                  className="w-full h-32 object-cover rounded-lg border border-slate-200" 
                />
                <button
                  onClick={() => onCustomBackgroundImageChange(null)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="背景画像を削除"
                >
                  <CloseIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
        
        {backgroundOption === 'custom_image' && !isPaidUser && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-orange-700 text-sm">
              ⭐️ この機能は有料プランでのみご利用いただけます。
            </p>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">2. 衣装</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            衣装選択
          </label>
          <select
            value={selectedOutfitId}
            onChange={(e) => {
              const outfitId = e.target.value;
              console.log('👔 Outfit selected:', outfitId);
              console.log('👔 Is custom_text?:', outfitId === 'custom_text');
              
              // 無料ユーザーが制限されたオプションを選択しようとした場合は阻止
              if (outfitId === 'custom_outfit_image' && !isPaidUser) {
                console.log('🚫 無料ユーザーがカスタム衣装画像を選択しようとしました');
                alert('この機能は有料プランでのみご利用いただけます。プランをアップグレードしてください。');
                return;
              }
              
              // 衣装変更時に、custom_outfit_image以外が選択された場合はカスタム画像をクリア
              if (outfitId !== 'custom_outfit_image' && customOutfitImage) {
                console.log('🧹 Clearing custom outfit image');
                onCustomOutfitImageChange(null);
              }
              
              onOutfitIdChange(outfitId);
            }}
            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {(() => {
              console.log('👔 衣装選択肢表示:', {
                isPaidUser,
                isTrialMode,
                totalOutfits: outfits.length,
                outfits: outfits.map(o => ({ id: o.id, name: o.name, isPremium: o.isPremium }))
              });
              
              return (
                <>
                  <optgroup label="基本衣装">
                    {outfits.filter(outfit => !outfit.isPremium && outfit.id !== 'custom_text').map((outfit) => (
                      <option key={outfit.id} value={outfit.id}>
                        {outfit.name}
                      </option>
                    ))}
                  </optgroup>
                  
                  <optgroup label="カスタム衣装">
                    <option value="custom_text">その他（テキストで指定）</option>
                    <option 
                      value="custom_outfit_image" 
                      disabled={!isPaidUser}
                      style={{ 
                        color: !isPaidUser ? '#9ca3af' : 'inherit',
                        backgroundColor: !isPaidUser ? '#f9fafb' : 'inherit'
                      }}
                    >
                      ⭐️その他（衣装を添付する）{!isPaidUser ? ' - 有料プラン限定' : ''}
                    </option>
                  </optgroup>
                </>
              );
            })()}
          </select>
          {selectedOutfitId && selectedOutfitId !== 'custom_text' && selectedOutfitId !== 'custom_outfit_image' && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                {outfits.find(o => o.id === selectedOutfitId)?.desc || '説明なし'}
              </p>
            </div>
          )}
        </div>
        
        {selectedOutfitId === 'custom_text' && !isFeatureRestricted('custom_text') && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              カスタム衣装の説明
            </label>
            <textarea
              rows={3}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="例: 上質な黒のタートルネックセーター"
              value={customOutfitText}
              onChange={(e) => {
                console.log('カスタム衣装入力:', e.target.value);
                onCustomOutfitTextChange(e.target.value);
              }}
            />
            <p className="text-xs text-slate-500 mt-1">
              衣装の詳細な説明を入力してください
            </p>
          </div>
        )}

        {selectedOutfitId === 'custom_text' && isFeatureRestricted('custom_text') && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <span className="font-medium">🔒 無料ユーザーは制限されています</span><br />
              カスタム衣装機能を使用するには、有料プランにアップグレードしてください。
            </p>
          </div>
        )}
        
        {selectedOutfitId === 'custom_outfit_image' && isPaidUser && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-slate-700 mb-2">衣装画像をアップロード</h3>
            <p className="text-sm text-slate-500 mb-4">
              オリジナルの衣装をアップロードして、その衣装を着て撮影されたような写真を生成できます。
            </p>
            {!customOutfitImage ? (
              <OutfitUploader onUpload={handleCustomOutfitUpload} />
            ) : (
              <div className="relative group">
                <img 
                  src={customOutfitImage.data} 
                  alt="Custom outfit" 
                  className="w-full h-32 object-cover rounded-lg border border-slate-200" 
                />
                <button
                  onClick={() => onCustomOutfitImageChange(null)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="衣装画像を削除"
                >
                  <CloseIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
        
        {selectedOutfitId === 'custom_outfit_image' && !isPaidUser && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-orange-700 text-sm">
              ⭐️ この機能は有料プランでのみご利用いただけます。
            </p>
          </div>
        )}
      </div>
      

      <div>
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">3. 顔の向き</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            顔の向き選択
          </label>
          <select
            value={selectedPoseId}
            onChange={(e) => {
              const poseId = e.target.value;
              console.log('Pose selected:', poseId);
              onPoseIdChange(poseId);
            }}
            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {poses.map((pose) => (
              <option key={pose.id} value={pose.id}>
                {pose.name}
              </option>
            ))}
          </select>
          {selectedPoseId && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                {poses.find(p => p.id === selectedPoseId)?.desc || '説明なし'}
              </p>
            </div>
          )}
        </div>
      </div>



      <button
        onClick={handleGenerateClick}
        disabled={isGenerateDisabled}
        className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <MagicWandIcon className="w-5 h-5" />
        <span>宣材写真を生成</span>
      </button>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
    </div>
  );
};